import { AlertCircle, AlertTriangle, Link2Off } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { InlineValidationState } from '@/services/inlineValidation';
import { cn } from '@/lib/utils';
import type { FlowTracingTruth } from '@/services/flowTracingTruth';

interface InlineValidationInsightsProps {
  analysis: InlineValidationState;
  onFocusNode?: (nodeId: string) => void;
  className?: string;
  dense?: boolean;
  liveStatus?: string | null;
  truthFactor?: FlowTracingTruth | null;
  compareSummary?: string | null;
  onRollbackLastGood?: () => void;
}

export function InlineValidationInsights({
  analysis,
  onFocusNode,
  className,
  dense = false,
  liveStatus = null,
  truthFactor = null,
  compareSummary = null,
  onRollbackLastGood,
}: InlineValidationInsightsProps) {
  const errorCount = analysis.suggestions.filter((s) => s.type === 'error').length;
  const warningCount = analysis.suggestions.filter((s) => s.type === 'warning').length;

  const truthDotClass =
    truthFactor?.state === 'match'
      ? 'bg-emerald-500'
      : truthFactor?.state === 'partial'
        ? 'bg-amber-500'
        : 'bg-red-500';

  if (analysis.suggestions.length === 0) {
    return (
      <section className={cn('space-y-2', className)}>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Link2Off className="h-3.5 w-3.5" />
          Inline validation & suggestions
        </div>
        <p className="text-xs text-muted-foreground">Map chain is connected. No inline fixes needed.</p>
        {truthFactor && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className={cn('inline-block h-2.5 w-2.5 rounded-full', truthDotClass)} aria-hidden />
            <span>{truthFactor.label}</span>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Link2Off className="h-3.5 w-3.5" />
          Inline validation & suggestions
        </div>
        <div className="flex items-center gap-1.5">
          {liveStatus && (
            <Badge variant="outline" className="text-[10px]">
              {liveStatus}
            </Badge>
          )}
          {truthFactor && (
            <Badge variant="outline" className="text-[10px]">
              <span className={cn('mr-1.5 inline-block h-2 w-2 rounded-full', truthDotClass)} aria-hidden />
              {truthFactor.label} · {Math.round(truthFactor.confidence * 100)}%
            </Badge>
          )}
          {errorCount > 0 && (
            <Badge variant="destructive" className="text-[10px]">
              {errorCount} error{errorCount > 1 ? 's' : ''}
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge variant="outline" className="text-[10px] border-amber-500/60 text-amber-700 dark:text-amber-300">
              {warningCount} warning{warningCount > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>

      <div className={cn('space-y-2', dense && 'space-y-1.5')}>
        {analysis.suggestions.slice(0, dense ? 4 : 6).map((suggestion) => (
          <div
            key={suggestion.id}
            className={cn(
              'rounded-lg border px-3 py-2',
              suggestion.type === 'error'
                ? 'border-red-500/45 bg-red-500/5'
                : 'border-amber-500/45 bg-amber-500/5'
            )}
          >
            <div className="flex items-start gap-2">
              {suggestion.type === 'error' ? (
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
              ) : (
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-foreground">{suggestion.title}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{suggestion.detail}</p>
                <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {suggestion.safety === 'safe-auto-fix'
                    ? 'Safe auto-fix'
                    : suggestion.safety === 'review-first'
                      ? 'Review before apply'
                      : 'Human input required'}
                </p>
              </div>
            </div>
            {suggestion.nodeId && onFocusNode && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-1 h-6 px-2 text-[11px]"
                onClick={() => onFocusNode(suggestion.nodeId!)}
              >
                Highlight on map
              </Button>
            )}
          </div>
        ))}
      </div>
      {truthFactor?.reasons?.length ? (
        <div className="rounded-md border border-border/50 bg-muted/20 px-3 py-2">
          <p className="text-[11px] font-medium text-foreground">Truth factor reasoning</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{truthFactor.reasons[0]}</p>
          {truthFactor.recommendations?.[0] && (
            <p className="mt-1 text-[11px] text-muted-foreground">Next: {truthFactor.recommendations[0]}</p>
          )}
          {compareSummary && <p className="mt-1 text-[11px] text-muted-foreground">{compareSummary}</p>}
          {onRollbackLastGood && (
            <Button variant="outline" size="sm" className="mt-2 h-6 px-2 text-[11px]" onClick={onRollbackLastGood}>
              Roll back to last good
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
}
